import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import NavigationBar from '../NavigationBar';
import Footer from '../Footer';
import keyService from '../../services/keyService';
import LocalStorageService from '../../services/localStorageService';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../i18n';
import './ScanQrCode.css';
import { tryDecrypt, tryDecryptWithMultipleKeys, tryDecryptWithPassphrase, parseEncryptionKey, isPassphraseProtected } from '../../utils/decryptionUtils';
import { readBarcodes } from 'zxing-wasm/reader';

const ScanQrCode = () => {
  const { state } = useAuth();
  const { t } = useTranslation();
  const uploadInputRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [scanningForKey, setScanningForKey] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [camera, setCamera] = useState('environment'); 
  const [encryptionKey, setEncryptionKey] = useState('');
  const [decryptedText, setDecryptedText] = useState(null);
  const [userKeys, setUserKeys] = useState([]);
  const [localKeys, setLocalKeys] = useState([]);
  const [allKeys, setAllKeys] = useState([]);
  const [isAutoDecrypting, setIsAutoDecrypting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isProcessingUpload, setIsProcessingUpload] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // Track the current step
  const [multiPartState, setMultiPartState] = useState({
    total: null,
    parts: {}, // { partNumber: chunkData }
    scanned: new Set() // Set of scanned part numbers
  });
  const [cameraReady, setCameraReady] = useState(false);
  const [manualCode, setManualCode] = useState(''); // Pasted SecurePaper code text
  const [manualKey, setManualKey] = useState(''); // Pasted/typed encryption key
  const [passphraseInput, setPassphraseInput] = useState(''); // For passphrase-protected documents
  const [copySuccess, setCopySuccess] = useState(false);

  // Refs for video stream management
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null); // Canvas for drawing QR code position overlays
  const streamRef = useRef(null);
  const isInitializingRef = useRef(false); // Prevent multiple simultaneous initializations
  const attachStreamRetryCountRef = useRef(0); // Track retry attempts
  const captureIntervalRef = useRef(null); // Interval for automatic frame capture
  const lastProcessedCodesRef = useRef(new Set()); // Track recently processed codes to avoid duplicates
  const scanningRef = useRef(scanning); // Track scanning state in ref
  const cameraReadyRef = useRef(cameraReady); // Track camera ready state in ref
  const isProcessingUploadRef = useRef(isProcessingUpload); // Track processing state in ref
  const detectedPositionsRef = useRef([]); // Track detected QR code positions for overlay


  // Fetch user keys and local keys
  useEffect(() => {
    const fetchKeys = async () => {
      let serverKeys = [];
      let localStorageKeys = [];

      // Fetch server keys if logged in
      if (state.isLoggedIn) {
        try {
          serverKeys = await keyService.getKeys();
          setUserKeys(serverKeys);
        } catch (err) {
          console.error("Error fetching user keys:", err);
        }
      }

      // Always fetch local keys (whether logged in or not)
      try {
        localStorageKeys = LocalStorageService.getKeys();
        setLocalKeys(localStorageKeys);
      } catch (err) {
        console.error("Error fetching local keys:", err);
      }

      // Combine all keys for auto-decryption
      const combinedKeys = [...serverKeys, ...localStorageKeys];
      setAllKeys(combinedKeys);
    };
    
    fetchKeys();
  }, [state.isLoggedIn]);

  // Add automatic decryption when key and encrypted content are available
  useEffect(() => {
    if (encryptionKey && result && !scanningForKey && !decryptedText && !isAutoDecrypting) {
      // Short delay to ensure all state updates have completed
      const timer = setTimeout(() => {
        handleDecrypt();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [encryptionKey, result, scanningForKey, decryptedText, isAutoDecrypting]);

  // Auto-decrypt when we have encrypted content and available keys
  useEffect(() => {
    if (result && allKeys.length > 0 && !decryptedText && !isAutoDecrypting) {
      // Only attempt auto-decryption if we haven't tried it yet or if the keys have changed
      console.log('Auto-decryption triggered with', allKeys.length, 'keys');
      tryDecryptWithAllKeys(result);
    }
  }, [result, allKeys, decryptedText, isAutoDecrypting]);

  // Parse multi-part code format: SP:1/3|chunkdata
  const parseMultiPartCode = (scannedData) => {
    if (scannedData.startsWith('SP:')) {
      const match = scannedData.match(/^SP:(\d+)\/(\d+)\|(.+)$/);
      if (match) {
        return {
          isMultiPart: true,
          partNumber: parseInt(match[1], 10),
          totalParts: parseInt(match[2], 10),
          chunkData: match[3]
        };
      }
    }
    return { isMultiPart: false, data: scannedData };
  };

  // Combine multi-part codes in order
  const combineMultiPartCodes = (parts, totalParts) => {
    if (Object.keys(parts).length !== totalParts) {
      return null; // Not all parts collected yet
    }
    
    let combined = '';
    for (let i = 1; i <= totalParts; i++) {
      if (!parts[i]) {
        return null; // Missing part
      }
      combined += parts[i];
    }
    return combined;
  };


  // Handle file upload for SecurePaper code
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setUploadedFile(file);
    processQRCodeFromImage(file);
  };

  // Function to detect multiple QR codes in an image using zxing-wasm
  const detectMultipleQRCodes = async (imageData) => {
    try {
      // Use zxing-wasm to read multiple barcodes from ImageData
      const results = await readBarcodes(imageData, {
        formats: ['QRCode'], // Only look for QR codes
        tryHarder: true, // Use more CPU to find codes
        maxNumberOfSymbols: 50, // Allow up to 50 codes
      });
      
      // Convert zxing-wasm results to a format compatible with our code
      return results.map(result => ({
        data: result.text,
        format: result.format,
        location: result.position
      }));
    } catch (error) {
      console.error('Error detecting QR codes with zxing-wasm:', error);
      throw error; // Re-throw to let caller handle the error
    }
  };

  // Process SecurePaper code from uploaded image
  const processQRCodeFromImage = async (file, sourceDimensions = null) => {
    setIsProcessingUpload(true);
    setError(null);
    
    try {
      // First, try using zxing-wasm directly with the file (more efficient)
      let qrResults;
      try {
        const results = await readBarcodes(file, {
          formats: ['QRCode'],
          tryHarder: true,
          maxNumberOfSymbols: 50,
        });
        
        qrResults = results.map(result => ({
          data: result.text,
          format: result.format,
          location: result.position
        }));
        
        // If we have source dimensions (from camera), store positions for overlay
        if (sourceDimensions && qrResults.length > 0) {
          console.log('Processing positions for overlay', {
            qrResultsCount: qrResults.length,
            sourceDimensions,
            firstResult: qrResults[0]
          });
          
          const positions = qrResults.map(result => {
            if (result.location) {
              console.log('QR result location:', result.location);
              // zxing-wasm returns position as an object with points array
              const points = result.location.points || 
                            (result.location.topLeft && result.location.topRight && result.location.bottomLeft && result.location.bottomRight ? 
                              [result.location.topLeft, result.location.topRight, result.location.bottomRight, result.location.bottomLeft] :
                              null);
              
              if (points && points.length >= 4) {
                return {
                  points: points,
                  sourceWidth: sourceDimensions.sourceWidth,
                  sourceHeight: sourceDimensions.sourceHeight
                };
              }
            }
            return null;
          }).filter(p => p !== null);
          
          console.log('Filtered positions:', positions);
          
          if (positions.length > 0) {
            detectedPositionsRef.current = positions;
            // Update overlay after a short delay to ensure canvas is ready
            requestAnimationFrame(() => {
              if (overlayCanvasRef.current && videoRef.current) {
                console.log('Updating overlay with positions');
                updateOverlay();
              } else {
                console.log('Cannot update overlay - canvas or video not ready', {
                  hasCanvas: !!overlayCanvasRef.current,
                  hasVideo: !!videoRef.current
                });
              }
            });
          }
        }
        
        console.log(`Found ${qrResults.length} QR code(s) using zxing-wasm`);
      } catch (zxingError) {
        console.warn('zxing-wasm failed with file, falling back to ImageData method:', zxingError);
        
        // Fallback: convert to ImageData and use that method
        const reader = new FileReader();
        const imageDataPromise = new Promise((resolve, reject) => {
          reader.onload = function(event) {
            const img = new Image();
            img.onload = function() {
              const canvas = document.createElement('canvas');
              const context = canvas.getContext('2d');
              canvas.width = img.width;
              canvas.height = img.height;
              context.drawImage(img, 0, 0, img.width, img.height);
              const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
              resolve(imageData);
            };
            img.onerror = reject;
            img.src = event.target.result;
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        
        const imageData = await imageDataPromise;
        qrResults = await detectMultipleQRCodes(imageData);
      }
      
      if (!qrResults || qrResults.length === 0) {
        setIsProcessingUpload(false);
        // Only show error for image uploads, not during camera scanning (to avoid blinking)
        if (!scanning) {
          setError(t('scan.errNoQrDetected'));
        }
        return;
      }
      
      console.log(`Processing ${qrResults.length} QR code(s) from image`);
      
      if (scanningForKey) {
        // For keys, only process the first code found
        const scannedData = qrResults[0].data;
        const parsed = parseMultiPartCode(scannedData);
        if (parsed.isMultiPart) {
          setIsProcessingUpload(false);
          setError(t('scan.errKeySplit'));
          return;
        }
        setEncryptionKey(scannedData);
        setScanningForKey(false);
        setScanning(false);
        setIsProcessingUpload(false);
      } else {
        // Process all found codes
        const multiPartCodes = [];
        let singlePartCode = null;
        
        for (const qrResult of qrResults) {
          const parsed = parseMultiPartCode(qrResult.data);
          if (parsed.isMultiPart) {
            multiPartCodes.push(parsed);
          } else {
            singlePartCode = parsed.data;
          }
        }
        
        // If we found multi-part codes, process them
        if (multiPartCodes.length > 0) {
          console.log(`Found ${multiPartCodes.length} multi-part code(s) in image`);
          
          // Use functional state update to ensure we work with latest state
          setMultiPartState(prevState => {
            // Determine the total from the first multi-part code found
            const firstCode = multiPartCodes[0];
            const newTotal = firstCode.totalParts;
            
            // If total parts changed or not set, reset everything
            let updatedParts = prevState.total !== newTotal ? {} : { ...prevState.parts };
            let updatedScanned = prevState.total !== newTotal ? new Set() : new Set(prevState.scanned);
            
            // Process all found multi-part codes
            for (const parsed of multiPartCodes) {
              updatedParts[parsed.partNumber] = parsed.chunkData;
              updatedScanned.add(parsed.partNumber);
              console.log(`Processed part ${parsed.partNumber} of ${parsed.totalParts} from image`);
            }
            
            // Check if we have all parts now
            if (updatedScanned.size === newTotal) {
              const combined = combineMultiPartCodes(updatedParts, newTotal);
              if (combined) {
                console.log('All parts collected from image, combining...');
                setIsProcessingUpload(false);
                // Use setTimeout to ensure state updates are complete
                setTimeout(() => {
                  setResult(combined);
                  setCurrentStep(2);
                  setScanning(false);
                  
                  // Try auto-decryption with all available keys
                  if (allKeys.length > 0) {
                    tryDecryptWithAllKeys(combined);
                  }
                }, 100);
              } else {
                setIsProcessingUpload(false);
              }
            } else {
              setIsProcessingUpload(false);
              // Only show error for image uploads, not during camera scanning (to avoid blinking)
              if (!scanning) {
                setError(t('scan.errPartsMissing', { found: updatedScanned.size, total: newTotal, missing: newTotal - updatedScanned.size }));
              }
            }
            
            return {
              total: newTotal,
              parts: updatedParts,
              scanned: updatedScanned
            };
          });
        } else if (singlePartCode) {
          // Single-part code
          setIsProcessingUpload(false);
          setResult(singlePartCode);
          
          // Reset multi-part state
          setMultiPartState({
            total: null,
            parts: {},
            scanned: new Set()
          });
          
          // Move to step 2 after successful detection
          setCurrentStep(2);
          setScanning(false);
          
          // Try auto-decryption with all available keys (server + local)
          if (allKeys.length > 0) {
            tryDecryptWithAllKeys(singlePartCode);
          }
        } else {
          setIsProcessingUpload(false);
        }
      }
    } catch (error) {
      console.error('Error processing image:', error);
      setIsProcessingUpload(false);
      setError(t('scan.errProcessImage', { message: error.message || t('scan.errUnknown') }));
    }
  };



  // Try to decrypt with all available keys (server + local)
  const tryDecryptWithAllKeys = async (encryptedText) => {
    if (!allKeys.length) {
      return;
    }

    // Passphrase-protected documents cannot be opened with stored keys
    if (isPassphraseProtected(encryptedText)) {
      return;
    }

    setIsAutoDecrypting(true);
    setError(null);

    const result = await tryDecryptWithMultipleKeys(encryptedText, allKeys);

    if (result.success) {
      setDecryptedText(result.decryptedText);
      setEncryptionKey(result.keyUsed);

      // If the key used is not from local storage, save it
      const keyUsedFromLocal = allKeys.find(k => k.encryption_key === result.keyUsed && k.isLocal);
      if (!keyUsedFromLocal) {
        saveKeyToLocalStorage(result.keyUsed, result.decryptedText);
      }
    } else {
      setError(result.error);
    }
    
    setIsAutoDecrypting(false);
  };


  // Stop automatic frame capture
  const stopAutoCapture = useCallback(() => {
    if (captureIntervalRef.current) {
      clearInterval(captureIntervalRef.current);
      captureIntervalRef.current = null;
    }
  }, []);

  // Update overlay canvas with detected QR code positions
  const updateOverlay = useCallback(() => {
    if (!overlayCanvasRef.current || !videoRef.current) {
      console.log('updateOverlay: canvas or video not available');
      return;
    }

    const overlay = overlayCanvasRef.current;
    const video = videoRef.current;
    
    // Get video display dimensions
    const videoRect = video.getBoundingClientRect();
    const displayWidth = videoRect.width;
    const displayHeight = videoRect.height;
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    
    console.log('updateOverlay called', {
      displayWidth,
      displayHeight,
      videoWidth,
      videoHeight,
      positionsCount: detectedPositionsRef.current.length
    });
    
    if (displayWidth === 0 || displayHeight === 0 || videoWidth === 0 || videoHeight === 0) {
      console.log('updateOverlay: invalid dimensions, skipping');
      return;
    }
    
    // Set overlay canvas size to match video display
    overlay.width = displayWidth;
    overlay.height = displayHeight;
    
    const ctx = overlay.getContext('2d');
    ctx.clearRect(0, 0, displayWidth, displayHeight);
    
    // Calculate scale factors
    const scaleX = displayWidth / videoWidth;
    const scaleY = displayHeight / videoHeight;
    
    console.log('Drawing positions:', detectedPositionsRef.current);
    
    // Draw rectangles for each detected QR code
    detectedPositionsRef.current.forEach((position, index) => {
      if (position.points && position.points.length >= 4) {
        console.log(`Drawing position ${index}:`, position.points);
        
        // Find bounding box from points
        // Handle both {x, y} format and array format
        const xs = position.points.map(p => (typeof p === 'object' && p.x !== undefined) ? p.x : (Array.isArray(p) ? p[0] : 0));
        const ys = position.points.map(p => (typeof p === 'object' && p.y !== undefined) ? p.y : (Array.isArray(p) ? p[1] : 0));
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);
        
        // Scale to display coordinates
        const x = minX * scaleX;
        const y = minY * scaleY;
        const width = (maxX - minX) * scaleX;
        const height = (maxY - minY) * scaleY;
        
        console.log(`Drawing rect at (${x}, ${y}) size ${width}x${height}`);
        
        // Draw rectangle
        ctx.strokeStyle = '#28a745';
        ctx.lineWidth = 3;
        ctx.setLineDash([]);
        ctx.strokeRect(x, y, width, height);
        
        // Draw corner markers
        const cornerSize = 15;
        ctx.fillStyle = '#28a745';
        // Top-left
        ctx.fillRect(x - 2, y - 2, cornerSize, 4);
        ctx.fillRect(x - 2, y - 2, 4, cornerSize);
        // Top-right
        ctx.fillRect(x + width - cornerSize + 2, y - 2, cornerSize, 4);
        ctx.fillRect(x + width - 2, y - 2, 4, cornerSize);
        // Bottom-left
        ctx.fillRect(x - 2, y + height - 2, cornerSize, 4);
        ctx.fillRect(x - 2, y + height - cornerSize + 2, 4, cornerSize);
        // Bottom-right
        ctx.fillRect(x + width - cornerSize + 2, y + height - 2, cornerSize, 4);
        ctx.fillRect(x + width - 2, y + height - cornerSize + 2, 4, cornerSize);
      } else {
        console.log(`Position ${index} has invalid points:`, position);
      }
    });
  }, []);

  // Effect to update overlay when video size changes
  useEffect(() => {
    if (cameraReady && videoRef.current && overlayCanvasRef.current) {
      const video = videoRef.current;
      const overlay = overlayCanvasRef.current;
      
      // Test: Draw a test rectangle to verify canvas is working
      const testDraw = () => {
        const rect = video.getBoundingClientRect();
        overlay.width = rect.width;
        overlay.height = rect.height;
        const ctx = overlay.getContext('2d');
        ctx.clearRect(0, 0, rect.width, rect.height);
        // Draw a test rectangle in the center
        ctx.strokeStyle = '#ff0000';
        ctx.lineWidth = 5;
        ctx.strokeRect(rect.width * 0.25, rect.height * 0.25, rect.width * 0.5, rect.height * 0.5);
        console.log('Test overlay drawn', { width: rect.width, height: rect.height });
      };
      
      const handleResize = () => {
        updateOverlay();
      };
      
      // Draw test overlay after a short delay
      setTimeout(testDraw, 500);
      
      // Update overlay on resize
      window.addEventListener('resize', handleResize);
      // Also update when video metadata loads
      video.addEventListener('loadedmetadata', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        video.removeEventListener('loadedmetadata', handleResize);
      };
    }
  }, [cameraReady, updateOverlay]);

  // Start scanning again
  const scanAgain = () => {
    stopCamera();
    setResult(null);
    setError(null);
    setDecryptedText(null);
    setEncryptionKey('');
    setScanningForKey(false);
    setScanning(false);
    setUploadedFile(null);
    setManualCode('');
    setManualKey('');
    setPassphraseInput('');
    setCopySuccess(false);
    setCurrentStep(1); // Go back to step 1
    setMultiPartState({
      total: null,
      parts: {},
      scanned: new Set()
    });
    // Clear detected positions
    detectedPositionsRef.current = [];
    if (overlayCanvasRef.current) {
      const ctx = overlayCanvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, overlayCanvasRef.current.width, overlayCanvasRef.current.height);
    }
  };

  // Start scanning specifically for a key
  const startKeyScan = () => {
    setScanningForKey(true);
    setScanning(true);
    setError(null);
    // Don't change step when scanning for key
  };

  // Save key to local storage after successful decryption
  const saveKeyToLocalStorage = (key, decryptedContent) => {
    try {
      // Check if key already exists in local storage
      const existingKeys = LocalStorageService.getKeys();
      const keyExists = existingKeys.some(existingKey => existingKey.encryption_key === key);
      
      if (!keyExists) {
        // Don't use decrypted content as subject - subject should be null/undefined for scanned keys
        const savedKey = LocalStorageService.saveKey({
          encryptionKey: key,
          name: t('scan.scannedKeyName', { date: new Date().toLocaleDateString() }),
          subject: null
        });

        // Update local keys state to include the new key
        setLocalKeys(prev => [...prev, savedKey]);
        setAllKeys(prev => [...prev, savedKey]);
        
        return savedKey;
      } else {
        console.log('Key already exists in local storage');
        return null;
      }
    } catch (error) {
      console.error('Error saving key to local storage:', error);
    }
  };

  // Handle decryption (uses a scanned/uploaded key, a manually entered one,
  // or - for passphrase-protected documents - the passphrase)
  const handleDecrypt = async () => {
    if (!result) {
      setError(t('scan.errProvideContentFirst'));
      return;
    }

    // Passphrase-protected documents: derive the key from the passphrase
    if (isPassphraseProtected(result)) {
      const passphrase = passphraseInput;
      if (!passphrase) {
        setError(t('scan.errEnterPassphrase'));
        return;
      }
      const decryptResult = await tryDecryptWithPassphrase(result, passphrase);
      if (decryptResult.success) {
        setDecryptedText(decryptResult.decryptedText);
        setError(null);
      } else {
        setError(decryptResult.error);
        setDecryptedText(null);
      }
      return;
    }

    const keyToUse = (encryptionKey || manualKey).trim();

    if (!keyToUse) {
      setError(t('scan.errProvideBoth'));
      return;
    }

    if (!parseEncryptionKey(keyToUse)) {
      setError(t('scan.errInvalidKeyFormat'));
      return;
    }

    const decryptResult = await tryDecrypt(result, keyToUse);

    if (decryptResult.success) {
      setDecryptedText(decryptResult.decryptedText);
      setEncryptionKey(keyToUse);
      setError(null);

      // Save the key to local storage if decryption was successful
      saveKeyToLocalStorage(keyToUse, decryptResult.decryptedText);
    } else {
      setError(decryptResult.error);
      setDecryptedText(null);
    }
  };

  // Accept a complete SecurePaper code (from paste) and move to the decrypt step
  const acceptManualCode = (code) => {
    setResult(code);
    setError(null);
    setMultiPartState({ total: null, parts: {}, scanned: new Set() });
    setCurrentStep(2);
    if (allKeys.length > 0) {
      tryDecryptWithAllKeys(code);
    }
  };

  // Handle manually pasted SecurePaper code text (single or multi-part)
  const handleManualCodeSubmit = (e) => {
    e.preventDefault();
    const input = manualCode.trim();
    if (!input) return;

    const tokens = input.split(/\s+/).filter(Boolean);
    const multiPartTokens = tokens.map(parseMultiPartCode).filter(p => p.isMultiPart);

    if (multiPartTokens.length === 0) {
      // Single code - joining tokens tolerates line breaks inside the pasted text
      acceptManualCode(tokens.join(''));
      return;
    }

    const totalParts = multiPartTokens[0].totalParts;
    const parts = {};
    multiPartTokens.forEach(p => { parts[p.partNumber] = p.chunkData; });
    const combined = combineMultiPartCodes(parts, totalParts);

    if (combined) {
      acceptManualCode(combined);
    } else {
      setError(t('scan.errPastedPartsMissing', { total: totalParts, pasted: Object.keys(parts).length }));
    }
  };

  // Copy decrypted content to clipboard
  const copyDecryptedText = () => {
    if (navigator.clipboard && decryptedText) {
      navigator.clipboard.writeText(decryptedText)
        .then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        })
        .catch(err => console.error('Failed to copy text: ', err));
    }
  };

  // Stop camera stream
  const stopCamera = useCallback(() => {
    // Stop automatic capture
    stopAutoCapture();

    // Reset initialization flag
    isInitializingRef.current = false;
    attachStreamRetryCountRef.current = 0;

    // Stop video stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Clear video element and event handlers
    if (videoRef.current) {
      videoRef.current.onloadedmetadata = null;
      videoRef.current.srcObject = null;
      videoRef.current.pause();
    }

    setCameraReady(false);
    setScanning(false);
  }, [stopAutoCapture]);

  // Capture and process frame automatically (called by interval)
  const captureAndProcessFrame = useCallback(async () => {
    // Use refs to check current state instead of stale closure values
    if (!videoRef.current || !canvasRef.current) {
      console.debug('Skipping capture - video or canvas not available');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Check if video is ready
    if (video.readyState !== 4 || video.videoWidth === 0 || video.videoHeight === 0) {
      console.debug('Skipping capture - video not ready', {
        readyState: video.readyState,
        width: video.videoWidth,
        height: video.videoHeight
      });
      return;
    }

    try {
      // Set canvas to match video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw current video frame to canvas
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob and process as image file
      canvas.toBlob(async (blob) => {
        if (!blob) {
          console.debug('Failed to create blob from canvas');
          return;
        }
        
        // Check scanning state again using ref (always current)
        if (!scanningRef.current) {
          console.debug('Skipping processing - scanning stopped');
          return;
        }
        
        // Double-check we're not already processing (race condition protection)
        if (isProcessingUploadRef.current) {
          console.debug('Skipping frame capture - already processing');
          return;
        }
        
        // Reduced logging to avoid console spam
        // console.log('Capturing frame for processing...', {
        //   blobSize: blob.size,
        //   videoWidth: video.videoWidth,
        //   videoHeight: video.videoHeight
        // });
        
        // Create a File object from the blob
        const file = new File([blob], 'camera-snapshot.png', { type: 'image/png' });
        
        // Process using the existing image upload logic with position tracking
        // The processQRCodeFromImage function will handle deduplication through multiPartState
        await processQRCodeFromImage(file, {
          sourceWidth: video.videoWidth,
          sourceHeight: video.videoHeight
        });
      }, 'image/png');
    } catch (error) {
      // Log errors for debugging
      console.error('Error capturing frame:', error);
    }
  }, [scanning, isProcessingUpload, processQRCodeFromImage]);

  // Update refs when state changes
  useEffect(() => {
    scanningRef.current = scanning;
  }, [scanning]);

  useEffect(() => {
    cameraReadyRef.current = cameraReady;
  }, [cameraReady]);

  useEffect(() => {
    isProcessingUploadRef.current = isProcessingUpload;
  }, [isProcessingUpload]);

  // Start automatic frame capture
  const startAutoCapture = useCallback(() => {
    // Clear any existing interval
    if (captureIntervalRef.current) {
      clearInterval(captureIntervalRef.current);
      captureIntervalRef.current = null;
    }

    console.log('Starting automatic frame capture', {
      scanning: scanningRef.current,
      cameraReady: cameraReadyRef.current,
      isProcessingUpload: isProcessingUploadRef.current
    });
    
    // Capture frames every 100ms (0.1 seconds)
    captureIntervalRef.current = setInterval(() => {
      // Check current state values from refs (always up to date)
      const currentScanning = scanningRef.current;
      const currentCameraReady = cameraReadyRef.current;
      const currentProcessing = isProcessingUploadRef.current;
      
      if (currentScanning && currentCameraReady && !currentProcessing) {
        // Reduced logging to avoid console spam
        // console.debug('Interval tick - calling captureAndProcessFrame');
        captureAndProcessFrame();
      }
      // Removed else logging to reduce console spam
    }, 100);
  }, [captureAndProcessFrame]);

  // Capture snapshot from camera and process it (manual button click)
  const captureSnapshot = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) {
      setError(t('scan.errCameraNotReady'));
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Check if video is ready
    if (video.readyState !== 4 || video.videoWidth === 0 || video.videoHeight === 0) {
      setError(t('scan.errVideoNotReady'));
      return;
    }

    try {
      setIsProcessingUpload(true);
      setError(null);
      
      // Set canvas to match video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw current video frame to canvas
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob and process as image file
      canvas.toBlob(async (blob) => {
        if (blob) {
          // Create a File object from the blob
          const file = new File([blob], 'camera-snapshot.png', { type: 'image/png' });
          // Process using the existing image upload logic
          await processQRCodeFromImage(file);
        } else {
          setIsProcessingUpload(false);
          setError(t('scan.errCaptureFailed'));
        }
      }, 'image/png');
    } catch (error) {
      console.error('Error capturing snapshot:', error);
      setIsProcessingUpload(false);
      setError(t('scan.errCaptureFailedWith', { message: error.message || t('scan.errUnknown') }));
    }
  }, [processQRCodeFromImage]);

  // Start camera - initialize the stream
  const initializeCamera = useCallback(async () => {
    // Prevent multiple simultaneous initializations
    if (isInitializingRef.current) {
      return;
    }

    isInitializingRef.current = true;
    setError(null);
    setCameraReady(false);
    attachStreamRetryCountRef.current = 0;

    try {
      // Stop any existing stream first
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      // Clear video element
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      // Request camera access
      const constraints = {
        video: {
          facingMode: camera,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Attach stream to video element (wait for it to be available)
      const attachStream = () => {
        // Limit retries to prevent infinite loops
        if (attachStreamRetryCountRef.current > 10) {
          console.error('Failed to attach stream after multiple retries');
          setError(t('scan.errInitCameraFailed'));
          setScanning(false);
          isInitializingRef.current = false;
          return;
        }

        if (videoRef.current && streamRef.current) {
          const video = videoRef.current;
          
          // Set srcObject
          video.srcObject = streamRef.current;

          // Set up metadata handler (replace any existing one)
          video.onloadedmetadata = () => {
            // Check if video is still the current one and stream is still active
            if (video === videoRef.current && streamRef.current) {
              console.log('Video metadata loaded, starting auto capture');
              setCameraReady(true);
              isInitializingRef.current = false;
              // Use setTimeout to ensure state is updated before starting capture
              setTimeout(() => {
                if (video === videoRef.current && streamRef.current) {
                  startAutoCapture();
                }
              }, 100);
            }
          };

          // Check if video is already ready
          if (video.readyState >= 2) {
            console.log('Video already ready, starting auto capture');
            setCameraReady(true);
            isInitializingRef.current = false;
            // Use setTimeout to ensure state is updated before starting capture
            setTimeout(() => {
              if (video === videoRef.current && streamRef.current) {
                startAutoCapture();
              }
            }, 100);
          } else {
            // Start playing video
            video.play().catch(err => {
              // Ignore AbortError - it's expected when switching cameras or stopping
              if (err.name !== 'AbortError' && video === videoRef.current) {
                console.error('Error playing video:', err);
                // Only set error if this is still the current video
                if (err.name !== 'NotAllowedError') {
                  setError(t('scan.errPlaybackFailed', { message: err.message || t('scan.errUnknown') }));
                }
              }
            });
          }
        } else {
          // Retry after a short delay if video element not ready
          attachStreamRetryCountRef.current++;
          setTimeout(attachStream, 100);
        }
      };

      // Wait a bit for video element to be ready
      setTimeout(attachStream, 50);
    } catch (err) {
      console.error('Error accessing camera:', err);
      isInitializingRef.current = false;
      let errorMessage = t('scan.errCameraAccess');

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage += t('scan.errAllowPermissions');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage += t('scan.errNoCamera');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage += t('scan.errCameraInUse');
      } else {
        errorMessage += err.message || t('scan.errUnknownOccurred');
      }
      
      setError(errorMessage);
      setScanning(false);
      setCameraReady(false);
    }
  }, [camera, startAutoCapture]);

  // Effect to initialize camera when scanning starts and video element is ready
  useEffect(() => {
    if (scanning && videoRef.current && !isInitializingRef.current) {
      // Small delay to ensure video element is fully mounted
      const timer = setTimeout(() => {
        if (scanning && videoRef.current && !isInitializingRef.current) {
          initializeCamera();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [scanning, camera]); // Removed initializeCamera from deps to prevent re-triggers

  // Effect to stop auto-capture when scanning stops
  useEffect(() => {
    if (!scanning) {
      stopAutoCapture();
    }
  }, [scanning, stopAutoCapture]);

  // Switch camera
  const switchCamera = useCallback(() => {
    const newCamera = camera === 'environment' ? 'user' : 'environment';
    setCamera(newCamera);
    // The useEffect will handle restarting the camera with the new facing mode
  }, [camera]);

  // Prevent background scrolling when camera overlay is active
  useEffect(() => {
    if (scanning) {
      document.body.classList.add('scanner-fullscreen-active');
    } else {
      document.body.classList.remove('scanner-fullscreen-active');
    }

    return () => {
      document.body.classList.remove('scanner-fullscreen-active');
    };
  }, [scanning]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  // Start scanner
  const startScanner = () => {
    setScanning(true);
    setError(null);
  };

  return (
    <>
      <Helmet>
        <title>Decrypt SecurePaper - Scan QR Code | SecurePaper App</title>
        <meta name="description" content="Decrypt your SecurePaper codes by scanning QR codes or uploading images. Retrieve your encrypted passwords, PINs, and sensitive information securely." />
        <meta name="keywords" content="decrypt SecurePaper, scan QR code, decrypt encrypted text, QR code scanner, decrypt password, secure decryption" />
        <meta property="og:title" content="Decrypt SecurePaper - Scan QR Code | SecurePaper App" />
        <meta property="og:description" content="Decrypt your SecurePaper codes by scanning QR codes or uploading images." />
        <meta property="og:url" content="https://app.securepaper.app/decrypt" />
        <link rel="canonical" href="https://app.securepaper.app/decrypt" />
      </Helmet>
      <NavigationBar />
      <div className="container scan-container">
        <div className="row">
          <Link className="back-link" to="/"><i className="fa fa-arrow-left" aria-hidden="true"></i> {t('common.backToHome')}</Link>
        </div>

        <div className="scan-header">
          <h1 className="text-center">{t('scan.title')}</h1>
          <p className="text-center text-muted">{t('scan.subtitle')}</p>
        </div>
        
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-8">
            <div className="scan-card">
              {/* Progress Steps */}
              <div className="scan-progress">
                <div className={`progress-step ${currentStep === 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                  <div className="step-number">
                    {currentStep > 1 ? <i className="fa fa-check" aria-hidden="true"></i> : 1}
                  </div>
                  <div className="step-label">{t('scan.step1')}</div>
                </div>
                <div className={`progress-line ${currentStep > 1 ? 'completed' : ''}`}></div>
                <div className={`progress-step ${currentStep === 2 ? 'active' : ''}`}>
                  <div className="step-number">2</div>
                  <div className="step-label">{t('scan.step2')}</div>
                </div>
              </div>
              
              {/* Step 1: Scan SecurePaper Code */}
              {currentStep === 1 && (
                <div className="section-container">
                  <div className="section-header">
                    <h3><i className="fa fa-qrcode" aria-hidden="true"></i> {t('scan.scanYour')}</h3>
                  </div>

                  <div className="section-content">
                    <p className="scan-instructions">
                      {t('scan.scanInstructions')}
                    </p>
                    
                    {error && (
                      <div className="scan-error-message">
                        <i className="fa fa-exclamation-circle" aria-hidden="true"></i> {error}
                      </div>
                    )}
                    
                    {scanning ? (
                      <div className="scanner-wrapper scanner-fullscreen">
                        {/* Multi-part progress indicator */}
                        {multiPartState.total && multiPartState.total > 1 && (
                          <div className="multi-part-progress">
                            <div className="progress-header">
                              <h4>{t('scan.multiPartTitle')}</h4>
                              <p>{t('scan.multiPartInstr', { total: multiPartState.total })}</p>
                            </div>
                            <div className="progress-indicator">
                              <div className="progress-bar">
                                <div 
                                  className="progress-fill" 
                                  style={{ width: `${(multiPartState.scanned.size / multiPartState.total) * 100}%` }}
                                ></div>
                              </div>
                              <div className="progress-text">
                                {t('scan.partsScanned', { scanned: multiPartState.scanned.size, total: multiPartState.total })}
                              </div>
                            </div>
                            <div className="parts-status">
                              {Array.from({ length: multiPartState.total }, (_, i) => i + 1).map(partNum => (
                                <div 
                                  key={partNum} 
                                  className={`part-indicator ${multiPartState.scanned.has(partNum) ? 'scanned' : 'pending'}`}
                                >
                                  <i className={`fa ${multiPartState.scanned.has(partNum) ? 'fa-check-circle' : 'fa-circle'}`} aria-hidden="true"></i>
                                  <span>{t('scan.part', { num: partNum })}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <div className="video-container">
                          <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            className={cameraReady ? 'camera-ready' : 'camera-loading'}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          {!cameraReady && (
                            <div className="camera-loading-overlay">
                              <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                              <p>{t('scan.initCamera')}</p>
                            </div>
                          )}
                          <div className="scanner-overlay">
                            <div className="scanner-frame"></div>
                          </div>
                          {/* Overlay canvas for QR code position indicators */}
                          <canvas 
                            ref={overlayCanvasRef} 
                            className="qr-overlay-canvas"
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              pointerEvents: 'none',
                              zIndex: 10
                            }}
                          />
                          {/* Hidden canvas for snapshot capture */}
                          <canvas ref={canvasRef} style={{ display: 'none' }} />
                        </div>
                        
                        <div className="scanner-actions">
                          <div className="scanning-status">
                            <i className="fa fa-camera" aria-hidden="true"></i> {t('scan.scanningAuto')}
                          </div>
                          <button 
                            className="btn btn-outline-secondary" 
                            onClick={switchCamera}
                          >
                            <i className="fa fa-arrows-rotate" aria-hidden="true"></i> {t('scan.switchCamera')}
                          </button>
                          <button 
                            className="btn btn-outline-danger" 
                            onClick={stopCamera}
                          >
                            <i className="fa fa-times" aria-hidden="true"></i> {t('scan.cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="scan-options">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="option-card" onClick={startScanner}>
                              <div className="option-icon">
                                <i className="fa fa-camera" aria-hidden="true"></i>
                              </div>
                              <h4>{t('scan.useCamera')}</h4>
                              <p>{t('scan.useCameraDesc')}</p>
                              <button type="button" className="btn btn-primary mt-2" onClick={startScanner}>
                                {t('scan.startCamera')}
                              </button>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div
                              className="option-card"
                              onClick={(e) => {
                                // The label opens the picker natively; only clicks
                                // outside it need the programmatic trigger
                                if (!e.target.closest('label')) {
                                  uploadInputRef.current?.click();
                                }
                              }}
                            >
                              <div className="option-icon">
                                <i className="fa fa-upload" aria-hidden="true"></i>
                              </div>
                              <h4>{t('scan.uploadImage')}</h4>
                              <p>{t('scan.uploadImageDesc')}</p>
                              <label className="btn btn-primary mt-2">
                                {t('scan.uploadImage')}
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="sr-only"
                                  ref={uploadInputRef}
                                  onChange={handleFileUpload}
                                />
                              </label>
                            </div>
                          </div>
                        </div>
                        
                        {isProcessingUpload && (
                          <div className="processing-status">
                            <i className="fa fa-spinner fa-spin" aria-hidden="true"></i> {t('scan.processing')}
                          </div>
                        )}

                        <div className="manual-code-entry mt-4">
                          <div className="text-center text-muted mb-2">
                            <small>{t('scan.orPasteCode')}</small>
                          </div>
                          <form onSubmit={handleManualCodeSubmit}>
                            <textarea
                              className="form-control"
                              rows="3"
                              placeholder={t('scan.pasteCodePlaceholder')}
                              value={manualCode}
                              onChange={(e) => setManualCode(e.target.value)}
                            />
                            <div className="text-center mt-2">
                              <button
                                type="submit"
                                className="btn btn-outline-primary"
                                disabled={!manualCode.trim()}
                              >
                                <i className="fa fa-arrow-right" aria-hidden="true"></i> {t('scan.continuePasted')}
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Step 2: Decrypt Content */}
              {currentStep === 2 && (
                <div className="section-container">
                  <div className="section-header">
                    <h3><i className="fa fa-unlock" aria-hidden="true"></i> {t('scan.decryptYourContent')}</h3>
                  </div>
                  
                  <div className="section-content">
                    {error && (
                      <div className="scan-error-message">
                        <i className="fa fa-exclamation-circle" aria-hidden="true"></i> {error}
                      </div>
                    )}
                    
                    {/* Show scanner when scanning for key in step 2 */}
                    {scanning && scanningForKey ? (
                      <div className="scanner-wrapper">
                        <div className="scanning-info">
                          <div className="scanning-mode-indicator key-scan">
                            <i className="fa fa-key" aria-hidden="true"></i> {t('scan.scanningForKey')}
                          </div>
                        </div>
                        
                        <div className="video-container">
                          <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            className={cameraReady ? 'camera-ready' : 'camera-loading'}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          {!cameraReady && (
                            <div className="camera-loading-overlay">
                              <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                              <p>{t('scan.initCamera')}</p>
                            </div>
                          )}
                          <div className="scanner-overlay">
                            <div className="scanner-frame"></div>
                          </div>
                          {/* Hidden canvas for frame capture */}
                          <canvas ref={canvasRef} style={{ display: 'none' }} />
                        </div>
                        
                        <div className="scanner-actions">
                          <div className="scanning-status">
                            <i className="fa fa-key" aria-hidden="true"></i> {t('scan.scanningForKeyAuto')}
                          </div>
                          <button 
                            className="btn btn-outline-secondary" 
                            onClick={switchCamera}
                          >
                            <i className="fa fa-arrows-rotate" aria-hidden="true"></i> {t('scan.switchCamera')}
                          </button>
                          <button 
                            className="btn btn-outline-danger" 
                            onClick={stopCamera}
                          >
                            <i className="fa fa-times" aria-hidden="true"></i> {t('scan.cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
  
                        
                        {allKeys.length > 0 && isAutoDecrypting && (
                          <div className="auto-decrypt-section">
                            {isAutoDecrypting ? (
                              <div className="auto-decrypt-status">
                                <i className="fa fa-spinner fa-spin" aria-hidden="true"></i> {t('scan.tryingSavedKeys')}
                              </div>
                            ) : null}
                          </div>
                        )}
                        
                        {!decryptedText && isPassphraseProtected(result) && (
                          <div className="decrypt-section">
                            <h4><i className="fa fa-user-secret" aria-hidden="true"></i> {t('scan.passphraseProtected')}</h4>
                            <p>{t('scan.passphraseProtectedDesc')}</p>
                            <form onSubmit={(e) => { e.preventDefault(); handleDecrypt(); }}>
                              <input
                                type="password"
                                className="form-control"
                                placeholder={t('scan.enterPassphrasePlaceholder')}
                                value={passphraseInput}
                                onChange={(e) => { setPassphraseInput(e.target.value); setError(null); }}
                                autoComplete="off"
                              />
                              <div className="decrypt-button-container">
                                <button
                                  type="submit"
                                  className="btn btn-primary btn-lg mt-3"
                                  disabled={!passphraseInput}
                                >
                                  <i className="fa fa-unlock" aria-hidden="true"></i> {t('scan.decryptContent')}
                                </button>
                              </div>
                            </form>
                          </div>
                        )}

                        {!decryptedText && !isPassphraseProtected(result) && (
                          <div className="decrypt-section">
                            <h4>{t('scan.enterKey')}</h4>
                            <p>{t('scan.enterKeyDesc')}</p>
                            
                            <div className="key-input-options">
                              <div className="row">
                                <div className="col-md-6">
                                  <div className="option-card">
                                    <div className="option-icon">
                                      <i className="fa fa-camera" aria-hidden="true"></i>
                                    </div>
                                    <h4>{t('scan.scanKey')}</h4>
                                    <p>{t('scan.scanKeyDesc')}</p>
                                    <button
                                      className="btn btn-primary mt-2"
                                      onClick={startKeyScan}
                                    >
                                      <i className="fa fa-camera" aria-hidden="true"></i> {t('scan.startCamera')}
                                    </button>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="option-card">
                                    <div className="option-icon">
                                      <i className="fa fa-upload" aria-hidden="true"></i>
                                    </div>
                                    <h4>{t('scan.uploadKeyFile')}</h4>
                                    <p>{t('scan.uploadKeyFileDesc')}</p>
                                    <label className="btn btn-primary mt-2">
                                      <i className="fa fa-upload" aria-hidden="true"></i> {t('scan.uploadKeyFile')}
                                      <input 
                                        type="file" 
                                         accept="image/*" 
                                         style={{ display: 'none' }} 
                                         onChange={handleFileUpload} 
                                         onClick={() => setScanningForKey(true)}
                                      />
                                    </label>
                                    {encryptionKey && encryptionKey.length > 0 && (
                                      <p className="key-status mt-2">
                                        <i className="fa fa-check-circle text-success" aria-hidden="true"></i> {t('scan.keyLoaded')}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="manual-key-entry mt-4">
                                <div className="text-center text-muted mb-2">
                                  <small>{t('scan.orPasteKey')}</small>
                                </div>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder={t('scan.pasteKeyPlaceholder')}
                                  value={manualKey}
                                  onChange={(e) => {
                                    setManualKey(e.target.value);
                                    // A manually entered key replaces any previously scanned one
                                    if (encryptionKey) setEncryptionKey('');
                                  }}
                                  autoComplete="off"
                                  spellCheck="false"
                                />
                              </div>

                              {/* Centralized decrypt button below both options */}
                              <div className="decrypt-button-container">
                                {encryptionKey && (
                                  <div className="current-key">
                                    <span>{t('scan.keyLoadedPrefix')}</span>
                                    <span className="key-preview">{encryptionKey.substr(0, 10)}...{encryptionKey.substr(-10)}</span>
                                  </div>
                                )}
                                <button
                                  className="btn btn-primary btn-lg mt-3"
                                  onClick={handleDecrypt}
                                  disabled={!encryptionKey && !manualKey.trim()}
                                >
                                  <i className="fa fa-unlock" aria-hidden="true"></i> {t('scan.decryptContent')}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    
                    {decryptedText && (
                      <div className="decrypted-content-section">
                        <h4><i className="fa fa-check-circle text-success" aria-hidden="true"></i> {t('scan.decryptionSuccessful')}</h4>
                        <div className="decrypted-content-box">
                          <div className="d-flex justify-content-between align-items-center">
                            <h4>{t('scan.decryptedContent')}</h4>
                            <button
                              className={`btn btn-sm ${copySuccess ? 'btn-success' : 'btn-outline-primary'}`}
                              onClick={copyDecryptedText}
                            >
                              <i className={`fa ${copySuccess ? 'fa-check' : 'fa-copy'}`} aria-hidden="true"></i>
                              {copySuccess ? ` ${t('scan.copied')}` : ` ${t('scan.copy')}`}
                            </button>
                          </div>
                          <div className="content-preview decrypted">
                            {decryptedText}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="scan-actions">
                      <button 
                        className="btn btn-outline-secondary"
                        onClick={scanAgain}
                      >
                        <i className="fa fa-arrows-rotate" aria-hidden="true"></i> {t('scan.decryptNewCode')}
                      </button>

                      <Link to="/" className="btn btn-outline-primary">
                        <i className="fa fa-home" aria-hidden="true"></i> {t('scan.backToDashboard')}
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ScanQrCode;

